import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import axios from 'axios'
import { toast } from 'react-toastify'
import { format } from 'date-fns'

const ApprovalsPage = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [approvals, setApprovals] = useState([])
  const [gyms, setGyms] = useState([])
  const [filters, setFilters] = useState({
    status: 'pending',
    type: '',
    gymId: ''
  })

  const isHeadOfAcquisition = user?.role?.level === 3
  const isCOO = user?.role?.level === 2

  useEffect(() => {
    if (isHeadOfAcquisition || isCOO) {
      fetchApprovals()
      fetchGyms()
    }
  }, [filters, isHeadOfAcquisition, isCOO])

  const fetchApprovals = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value)
      })
      
      const response = await axios.get(`/api/approvals?${params.toString()}`)
      setApprovals(response.data.approvals || [])
    } catch (error) {
      console.error('Error fetching approvals:', error)
      toast.error('Failed to load approvals')
    } finally {
      setLoading(false)
    }
  }

  const fetchGyms = async () => {
    try {
      const response = await axios.get('/api/gyms')
      setGyms(response.data.gyms || [])
    } catch (error) {
      console.error('Error fetching gyms:', error)
    }
  }

  if (!isHeadOfAcquisition && !isCOO) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Approvals</h1>
          <p className="mt-1 text-sm text-gray-600">
            Sequential approval workflow management
          </p>
        </div>

        <div className="card">
          <div className="card-body">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Approval Workflow (Coming Soon)
            </h2>
            <p className="text-gray-600">
              This page will show the sequential approval system: Facility → Head → COO → CEO
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* HOA Approval Quick Link */}
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-orange-800">HOA Gym Approvals</p>
          <p className="text-xs text-orange-600 mt-0.5">Review gyms in Internal Approval stage — Approve, Hold, or Reject</p>
        </div>
        <a href="/hoa-approvals"
          className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
          Open →
        </a>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gym Progress Approvals</h1>
          <p className="mt-1 text-sm text-gray-600">
            Approve gym stage transitions up to Facility Work
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
          <span className="text-sm font-medium text-blue-800">{isCOO ? 'COO' : 'Head of Acquisition'}</span>
        </div>
      </div>

      {/* Permission Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Approval Authority
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>As Head of Acquisition, you can approve gym progress through:</p>
              <ul className="mt-1 list-disc list-inside space-y-1">
                <li><strong>Site Visit → Proposal Sent</strong></li>
                <li><strong>Proposal Sent → Agreement Signed</strong></li>
                <li><strong>Agreement Signed → Facility Work</strong></li>
              </ul>
              <p className="mt-2 text-xs">
                <em>Note: Approval to Go-Live stages requires COO/CEO approval</em>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="pending">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="stage_change">Stage Changes</option>
              <option value="checklist_item">Checklist Items</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gym</label>
            <select
              value={filters.gymId}
              onChange={(e) => setFilters(prev => ({ ...prev, gymId: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Gyms</option>
              {gyms.map(gym => (
                <option key={gym.id} value={gym.id}>
                  {gym.name} - {gym.city}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Approvals List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="space-y-4">
          {approvals.length > 0 ? (
            approvals.map(approval => (
              <ApprovalCard key={approval.id} approval={approval} />
            ))
          ) : (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Approvals</h3>
              <p className="text-gray-600">No approvals found matching your filters.</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const ApprovalCard = ({ approval }) => {
  const getStatusBadge = (status) => {
    const badges = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    }
    return badges[status] || 'bg-gray-100 text-gray-800'
  }

  const getTypeBadge = (type) => {
    const badges = {
      'stage_change': 'bg-blue-100 text-blue-800',
      'checklist_item': 'bg-purple-100 text-purple-800',
      'go_live': 'bg-orange-100 text-orange-800'
    }
    return badges[type] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {approval.gym?.name || 'Unknown Gym'}
            </h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(approval.status)}`}>
              {approval.status}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeBadge(approval.type)}`}>
              {approval.type.replace('_', ' ')}
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div>
              <span className="font-medium">City:</span> {approval.gym?.city || 'N/A'}
            </div>
            <div>
              <span className="font-medium">Current Stage:</span> {approval.gym?.stage || 'N/A'}
            </div>
            <div>
              <span className="font-medium">Requested:</span> {format(new Date(approval.createdAt), 'MMM dd, yyyy')}
            </div>
          </div>
        </div>
      </div>

      {approval.comments && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-700">{approval.comments}</p>
        </div>
      )}

      {approval.approvedBy && (
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>
            {approval.status} by {approval.approvedBy.name}
          </span>
          <span>
            {approval.approvedAt && format(new Date(approval.approvedAt), 'MMM dd, yyyy HH:mm')}
          </span>
        </div>
      )}
    </div>
  )
}

export default ApprovalsPage 